import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import ApartmentTwoToneIcon from "@mui/icons-material/ApartmentTwoTone";
import MapTwoToneIcon from "@mui/icons-material/MapTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";

const TabelaGerentes = ({
  gerentes = [],
  loading = false,
  onEdit = () => {},
  onTelefoneClick = () => {},
  onDelete = () => {},
  showEdit = true,
  showDelete = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const columnsCount = 5 + (showEdit ? 1 : 0) + (showDelete ? 1 : 0);

  const sanitizeNumber = (value) => (value || "").toString().replace(/\D/g, "");

  const handlePhoneClick = (gerente, phoneValue) => {
    const sanitized = sanitizeNumber(phoneValue);
    if (!sanitized) return;

    onTelefoneClick({
      ...gerente,
      telefone: phoneValue,
      whatsAppId: sanitized,
      tipo: gerente.tipo || "Gestor",
    });
  };

  const renderPhone = (gerente, phoneValue) => {
    if (!phoneValue) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    return (
      <Box
        onClick={() => handlePhoneClick(gerente, phoneValue)}
        sx={{
          cursor: "pointer",
          display: "inline-block",
          bgcolor: "rgba(102, 126, 234, 0.1)",
          color: "#667eea",
          px: 1.25,
          py: 0.5,
          borderRadius: 2,
          fontSize: "0.85rem",
          fontWeight: 600,
          border: "1px solid rgba(102, 126, 234, 0.2)",
          transition: "all 0.2s ease-in-out",
          maxWidth: 140,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": {
            bgcolor: "rgba(102, 126, 234, 0.2)",
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          },
        }}
        title={phoneValue}
      >
        {phoneValue}
      </Box>
    );
  };

  const renderImobiliarias = (imobiliariaRaw, gerenteNome) => {
    const imobiliarias = (imobiliariaRaw || "")
      .split("|")
      .map((item) => item.trim())
      .filter(Boolean);

    if (imobiliarias.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    if (imobiliarias.length <= 2) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {imobiliarias.join(", ")}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "inline-flex", justifyContent: "center", minWidth: "11ch" }}>
        <Tooltip title="Ver imobiliarias" arrow>
          <IconButton
            size="small"
            onClick={() => {
              setModalItems(imobiliarias);
              setModalTitle(
                gerenteNome ? `Imobiliarias de ${gerenteNome}` : "Imobiliarias"
              );
              setModalOpen(true);
            }}
            sx={{
              border: "1px solid #667eea",
              color: "#667eea",
              background: "rgba(102, 126, 234, 0.08)",
              borderRadius: "50%",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.2)",
                borderColor: "#667eea",
              },
            }}
          >
            <ApartmentTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderRegioes = (regiaoRaw, gerenteNome) => {
    const regioes = (regiaoRaw || "")
      .split("|")
      .map((item) => item.trim())
      .filter(Boolean);

    if (regioes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    if (regioes.length <= 2) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {regioes.join(", ")}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "inline-flex", justifyContent: "center", minWidth: "6ch" }}>
        <Tooltip title="Ver regioes" arrow>
          <IconButton
            size="small"
            onClick={() => {
              setModalItems(regioes);
              setModalTitle(
                gerenteNome ? `Regioes de ${gerenteNome}` : "Regioes"
              );
              setModalOpen(true);
            }}
            sx={{
              border: "1px solid #667eea",
              color: "#667eea",
              background: "rgba(102, 126, 234, 0.08)",
              borderRadius: "50%",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.2)",
                borderColor: "#667eea",
              },
            }}
          >
            <MapTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        bgcolor: "background.paper",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 12px rgba(0,0,0,0.28)"
            : "0 2px 12px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "divider",
        overflowX: isMobile ? "auto" : "hidden",
      }}
      aria-label="Tabela com lista de gerentes"
    >
      <Table sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow
            sx={{
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(102, 126, 234, 0.16) 0%, rgba(118, 75, 162, 0.18) 100%)"
                  : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
              "& .MuiTableCell-head": {
                fontWeight: "bold",
                fontSize: "0.9rem",
                color: "text.primary",
                letterSpacing: "0.5px",
                py: 2,
                borderBottom: "2px solid",
                borderColor: "divider",
              },
            }}
          >
            <TableCell>Nome</TableCell>
            <TableCell>Celular</TableCell>
            <TableCell>Imobiliária</TableCell>
            <TableCell>Região</TableCell>
            {showEdit && <TableCell>Editar</TableCell>}
            {showDelete && <TableCell>Excluir</TableCell>}
            <TableCell>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Chip
                  label={`${gerentes.length} ${
                    gerentes.length === 1 ? "Gerente" : "Gerentes"
                  }`}
                  onClick={() => {}}
                  clickable={false}
                  sx={{
                    bgcolor: "action.selected",
                    color: "text.primary",
                    fontWeight: "medium",
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columnsCount} align="center">
                <Box sx={{ py: 5 }}>
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2, color: "text.secondary" }}>
                    Carregando gerentes...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : gerentes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnsCount} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum gerente encontrado.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            gerentes.map((gerente, index) => (
              <TableRow
                key={`${gerente.uniqueKey ?? gerente.matchKey ?? gerente.nome ?? "gerente"}-${index}`}
                sx={{
                  "&:nth-of-type(even)": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                  },
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" color="text.primary">
                    {gerente.nome || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {renderPhone(gerente, gerente.celular || gerente.telefone)}
                </TableCell>
                <TableCell>
                  {renderImobiliarias(gerente.imobiliaria, gerente.nome)}
                </TableCell>
                <TableCell>
                  {renderRegioes(gerente.regiao, gerente.nome)}
                </TableCell>
                {showEdit && (
                  <TableCell>
                    <IconButton
                      size="small"
                      sx={{
                        border: "1px solid #667eea",
                        color: "#667eea",
                        background: "rgba(102, 126, 234, 0.08)",
                        borderRadius: "50%",
                        "&:hover": {
                          background: "rgba(102, 126, 234, 0.2)",
                          borderColor: "#667eea",
                        },
                      }}
                      onClick={() => onEdit(gerente)}
                    >
                      <EditTwoToneIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
                {showDelete && (
                  <TableCell>
                    <Tooltip title="Excluir gerente" arrow>
                      <IconButton
                        size="small"
                        aria-label="Excluir gerente"
                        sx={{
                          border: "1px solid #f44336",
                          color: "#f44336",
                          background: "rgba(244, 67, 54, 0.08)",
                          borderRadius: "50%",
                          "&:hover": {
                            background: "rgba(244, 67, 54, 0.2)",
                            borderColor: "#f44336",
                          },
                        }}
                        onClick={() => onDelete(gerente)}
                      >
                        <DeleteTwoToneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{modalTitle || "Imobiliarias"}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableBody>
              {modalItems.map((imob, idx) => (
                <TableRow key={`${imob}-${idx}`}>
                  <TableCell>
                    <Typography variant="body2">{imob}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </TableContainer>
  );
};

export default TabelaGerentes;
